/**
 * The document archive.
 *
 * A PDF is filed when it is printed against a deal that has been sold or
 * sold. Quotes and deposits print without being filed — that is the noisy
 * phase, where the same packet gets run off three times while a typo is fixed,
 * and none of those copies is the one anybody signs.
 *
 * Reprinting reads the stored bytes back. It never rebuilds the document. That
 * is the whole point: a packet printed in March still prints in November the
 * way it did in March, after the template has been revised and the fees have
 * moved.
 */

import { supabase } from "./supabase";

export const BUCKET = "documents";

export interface ArchivedDoc {
  id: string;
  doc_type: string;
  storage_path: string;
  generated_at: string;
  byte_size: number | null;
  deal_status: string | null;
}

/** Human labels for the things that actually get filed. */
export const DOC_TYPE_LABELS: Record<string, string> = {
  retail_packet: "Retail packet",
  deposit_receipt: "Deposit receipt",
  payment_receipt: "Payment receipt",
  paid_in_full_receipt: "Paid-in-full receipt",
  we_owe: "We owe / due bill",
  charge_receipt: "Card payment receipt",
};

export const labelForDocType = (t: string) =>
  DOC_TYPE_LABELS[t] ?? t.replace(/_/g, " ");

/**
 * Thrown when the file could not be filed. Printing has already succeeded by
 * this point, so this is a warning to show, not a failure to abort on — but it
 * is never swallowed. A document silently not being archived is exactly the
 * kind of thing you discover ten years too late.
 */
export class ArchiveError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = "ArchiveError";
  }
}

/** Storage keys are ASCII, and a deal number is not guaranteed to be. */
const safe = (s: string) => s.replace(/[^A-Za-z0-9._-]/g, "-");

function pathFor(dealId: string, docType: string, when: Date) {
  const stamp = when.toISOString().replace(/[:.]/g, "-");
  return `deals/${dealId}/${safe(docType)}-${stamp}.pdf`;
}

/**
 * File a generated PDF against a deal.
 *
 * Upload first, row second. If the row fails the uploaded object is removed,
 * because an object with nothing pointing at it is invisible to the app and
 * counts against storage forever.
 */
export async function archiveDealDocument(
  bytes: Uint8Array,
  opts: { dealId: string; docType: string; dealStatus: string },
): Promise<ArchivedDoc> {
  const when = new Date();
  const path = pathFor(opts.dealId, opts.docType, when);

  // A Uint8Array may be a view onto a larger buffer; copy before handing it
  // to Blob, the same reason openForPrint does.
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const blob = new Blob([buffer], { type: "application/pdf" });

  const up = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: "application/pdf", upsert: false });

  if (up.error) {
    const msg = up.error.message;
    if (/bucket/i.test(msg) && /not found|does not exist/i.test(msg)) {
      throw new ArchiveError(
        "The documents bucket does not exist yet. Run 10_document_archive.sql "
          + "in Supabase.",
        up.error,
      );
    }
    throw new ArchiveError(`Could not store the file: ${msg}`, up.error);
  }

  const { data, error } = await supabase
    .from("documents")
    .insert({
      deal_id: opts.dealId,
      doc_type: opts.docType,
      storage_path: path,
      byte_size: blob.size,
      deal_status: opts.dealStatus,
      generated_at: when.toISOString(),
    })
    .select("id,doc_type,storage_path,generated_at,byte_size,deal_status")
    .single();

  if (error || !data) {
    await supabase.storage.from(BUCKET).remove([path]);
    throw new ArchiveError(
      `The file was stored but could not be filed: ${error?.message ?? "no row returned"}`,
      error,
    );
  }

  return data as ArchivedDoc;
}

/** Everything filed against a deal, newest first. */
export async function listDealDocuments(dealId: string): Promise<ArchivedDoc[]> {
  const { data, error } = await supabase
    .from("documents")
    .select("id,doc_type,storage_path,generated_at,byte_size,deal_status")
    .eq("deal_id", dealId)
    .order("generated_at", { ascending: false });

  if (error) throw new ArchiveError(`Could not read the archive: ${error.message}`);
  return (data ?? []) as ArchivedDoc[];
}

/**
 * Fetch a filed document's bytes. Private bucket, so this goes through the
 * signed-in session rather than a URL that could be forwarded.
 */
export async function fetchArchivedDocument(path: string): Promise<Uint8Array> {
  const { data, error } = await supabase.storage.from(BUCKET).download(path);
  if (error || !data) {
    throw new ArchiveError(
      `Could not retrieve the filed document: ${error?.message ?? "not found"}`,
      error,
    );
  }
  return new Uint8Array(await data.arrayBuffer());
}

/** A filename a person would recognise in a downloads folder. */
export function filenameFor(doc: ArchivedDoc, dealNo: string) {
  const day = doc.generated_at.slice(0, 10);
  return `${safe(dealNo)}-${safe(doc.doc_type)}-${day}.pdf`;
}

/** Size for display. Files here are hundreds of KB, not bytes. */
export function formatSize(bytes: number | null): string {
  if (bytes === null || bytes <= 0) return "";
  const mb = bytes / 1048576;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}
